import * as React from "react";
import { SettingType, SettingValueType } from "../misc/api/apiTypes";
import { TextBox } from "../forms/informedv4/TextBoxV4";
import { TextArea } from "../forms/informedv4/TextAreaV4";
import { PrimitiveMultiSelect } from "../forms/informedv4/MultiSelectV4";
import { Checkbox } from "../forms/informedv4/CheckboxV4";
import { TimePicker } from "../forms/informedv4/TimePickerV4";
import { Tags } from "../forms/informedv4/Tags";
import { useArrayField, useFieldApi, useFormState, useFieldState } from "informedv4";
import { Required } from "../forms/validation/Required";
import './settings-control-renderer.css';

// Helper function to get nested value from object by path
const getValueByPath = (obj: Object, path: string) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((current, key) => current?.[key], obj);
};

// Generic validation function for number fields
const createNumberValidator = (min?: number, max?: number, dynamicMax?: number, required?: boolean) => {
    return (value: any) => {
        if (value === undefined || value === null || value === '') {
            return required ? 'This field is required' : undefined;
        }
        const numValue = Number(value);

        if (isNaN(numValue)) {
            return 'Please enter a valid number';
        }

        // Determine the effective max
        // If dynamicMax exists and is less than the static max(8), use dynamicMax
        // If dynamicMax is invalid (negative, zero, NaN), ignore it and fall back to static max
        let effectiveMax = max;
        if (dynamicMax !== undefined && !isNaN(dynamicMax) && dynamicMax > 0) {
            if (max !== undefined && dynamicMax >= max) {
                effectiveMax = max;
            } else {
                effectiveMax = dynamicMax;
            }
        }

        if (min !== undefined && numValue < min) {
            if (effectiveMax !== undefined) {
                return `Valid range is ${min} to ${effectiveMax}`;
            }
            return `Value must be at least ${min}`;
        }

        if (effectiveMax !== undefined && numValue > effectiveMax) {
            if (min !== undefined) {
                return `Valid range is ${min} to ${effectiveMax}`;
            }
            return `Value must be at most ${effectiveMax}`;
        }
        return undefined;
    };
};

export const RenderSettingControl = ({ settingDefinition, settingKey, parentSectionKey }) => {
    if (settingDefinition.editable == false)
        return null;

    const required = settingDefinition.required !== null && settingDefinition.required !== undefined
        ? settingDefinition.required
        : undefined;

    const controlPath = `${parentSectionKey}.${settingKey}`;

    var field = useFieldApi(controlPath);
    var fieldValue = field.getValue() as any;

    const formState = useFormState();

    const hasDependency = !!settingDefinition.dependsOn;
    let isDisabled = false;

    if (hasDependency) {
        const dependsOnPath = `${parentSectionKey}.${settingDefinition.dependsOn}`;
        const dependsOnValue = getValueByPath(formState.values, dependsOnPath);
        const isDependencyMet = Array.isArray(settingDefinition.dependsOnValue) ? dependsOnValue.some(r => settingDefinition.dependsOnValue.includes(r)) : dependsOnValue === settingDefinition.dependsOnValue;
        isDisabled = !isDependencyMet;
    }

    // Handle dynamic max value using naming convention
    let dynamicMaxValue: number | undefined = undefined;
    if (settingDefinition.dynamicMax) {
        const maxTestsPath = `${parentSectionKey}.${settingDefinition.dynamicMax}`;
        const maxTestsValue = getValueByPath(formState.values, maxTestsPath);
        if (maxTestsValue !== undefined && maxTestsValue !== null && !isNaN(Number(maxTestsValue))) {
            dynamicMaxValue = Number(maxTestsValue);
        }
    }

    // Reset to default value when disabled and clear validation errors
    React.useEffect(() => {
        if (isDisabled && fieldValue !== settingDefinition.defaultValue) {
            field.setValue(settingDefinition.defaultValue);
            field.validate();
        }
    }, [isDisabled, fieldValue, settingDefinition.defaultValue, field]);

    var valueChanged = false;

    if (Array.isArray(settingDefinition.defaultValue)) {
        if (fieldValue == undefined) {
            valueChanged = settingDefinition.defaultValue.length !== 0;
        }
        else {
            valueChanged = (settingDefinition.defaultValue.length != fieldValue?.length) ||
                !settingDefinition.defaultValue.every((value, index) => value == fieldValue[index])
        }
    } else {
        valueChanged = settingDefinition.defaultValue != fieldValue
    }

    if (settingDefinition.selectable) {
        return (
            <div className="form-group col-md-6 col-sm-6 col-xs-12 setting-input">
                <label htmlFor={controlPath}>
                    {settingDefinition.label}
                    {
                        valueChanged ? <i className="fas fa-pen" data-bs-toggle="tooltip" title="Setting Changed" /> :
                            null
                    }
                </label>
                <p className="help-block">{settingDefinition.helpText}</p>
                <PrimitiveMultiSelect
                    name={controlPath}
                    id={controlPath}
                    field={controlPath}
                    options={settingDefinition.datasource}
                    labelKey="text"
                    valueKey="value"
                    allowSelectAll={settingDefinition.valueType == SettingValueType.Collection}
                    multi={settingDefinition.valueType == SettingValueType.Collection}
                    validate={value => (required !== false ? Required(value) : undefined)}
                />
            </div>
        )
    }

    switch (settingDefinition.type) {
        case SettingType.String:
            if (settingDefinition.valueType === SettingValueType.Collection) {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12 setting-input">
                        <label htmlFor={controlPath}>
                            {settingDefinition.label}
                            {
                                valueChanged ? <i className="fas fa-pen" data-bs-toggle="tooltip" title="Setting Changed" /> :
                                    null
                            }
                        </label>
                        <p className="help-block">{settingDefinition.helpText}</p>
                        <Tags name={controlPath} field={controlPath} />
                    </div>
                )
            }
            if (settingDefinition.valueType === SettingValueType.One) {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12 setting-input largen-height">
                        <label htmlFor={controlPath}>
                            {settingDefinition.label}
                            {
                                valueChanged ? <i className="fas fa-pen" data-bs-toggle="tooltip" title="Setting Changed" /> :
                                    null
                            }
                        </label>
                        <p className="help-block">{settingDefinition.helpText}</p>
                        <TextBox name={controlPath} id={controlPath} field={controlPath} allowEmptyString={true} required={required === true} />
                    </div>
                )
            }
        case SettingType.MultiLineString:
            if (settingDefinition.valueType === SettingValueType.One) {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12 setting-input largen-height">
                        <label htmlFor={controlPath}>
                            {settingDefinition.label}
                            {
                                valueChanged ? <i className="fas fa-pen" data-bs-toggle="tooltip" title="Setting Changed" /> :
                                    null
                            }
                        </label>
                        <p className="help-block">{settingDefinition.helpText}</p>
                        <TextArea name={controlPath} id={controlPath} field={controlPath} allowEmptyString={true} required={required === true} />
                    </div>
                )
            }
        case SettingType.Boolean:
            return (

                <div className="form-group col-md-6 col-sm-6 col-xs-12 setting-input largen-height">
                    <Checkbox name={controlPath} id={controlPath} field={controlPath} label={settingDefinition.label} labelSuffix={() => valueChanged ? <i className="fas fa-pen" data-bs-toggle="tooltip" title="Setting Changed" /> :
                        null} />
                    <p className="help-block">{settingDefinition.helpText}</p>
                </div>
            )
        case SettingType.Number:
            return (
                <div className="form-group col-md-6 col-sm-6 col-xs-12 setting-input largen-height">
                    <label htmlFor={controlPath}>
                        {settingDefinition.label}
                        {
                            valueChanged ? <i className="fas fa-pen" data-bs-toggle="tooltip" title="Setting Changed" /> :
                                null
                        }
                    </label>
                    <p className="help-block">{settingDefinition.helpText}</p>
                    <TextBox type="number" name={controlPath} id={controlPath} field={controlPath} min={settingDefinition?.min} max={settingDefinition?.max} allowEmptyString={required !== false} required={required !== false}
                        disabled={isDisabled}
                        validate={createNumberValidator(
                            settingDefinition.min,
                            settingDefinition.max,
                            dynamicMaxValue,
                            required !== false
                        )} />
                </div>
            )
        case SettingType.Time:
            const arrayFieldApiRef = React.useRef();
            if (settingDefinition.valueType == SettingValueType.Collection) {
                const { render, arrayFieldApi, arrayFieldState } = useArrayField({ name: controlPath, arrayFieldApiRef: arrayFieldApiRef, } as any);
                return (
                    <>
                        {arrayFieldState.fields.map(({ arrayFieldItemState, arrayFieldItemApi }, i) => (
                            <div className="form-group col-md-6 col-sm-6 col-xs-12 setting-input largen-height" key={arrayFieldItemState.key}>
                                <label htmlFor={arrayFieldItemState.name}>
                                    {settingDefinition.label}
                                </label>
                                <TimePicker
                                    key={arrayFieldItemState.key}
                                    id={arrayFieldItemState.name}
                                    field={arrayFieldItemState.name}
                                    label=""
                                    placeholderText="HH:mm (24 hour format)"
                                    dateFormat="HH:mm:ss"
                                    required={required !== false}
                                    suffix={<i className="text-danger fas fa-times" onClick={() => arrayFieldItemApi.remove()} style={{ cursor: 'pointer' }} />}
                                />
                            </div>
                        ))}
                        <div className="col-md-12">
                            <button className="btn btn-primary btn-outline" type="button" onClick={() => arrayFieldApi.add()}>Add {settingDefinition.label}</button>
                        </div>
                    </>
                )
            }
            if (settingDefinition.valueType === SettingValueType.One) {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12 setting-input largen-height">
                        <label htmlFor={controlPath}>
                            {settingDefinition.label}
                            {
                                valueChanged ? <i className="fas fa-pen" data-bs-toggle="tooltip" title="Setting Changed" /> :
                                    null
                            }
                        </label>
                        <TimePicker
                            key={controlPath}
                            id={controlPath}
                            field={controlPath}
                            label=""
                            placeholderText="HH:mm (24 hour format)"
                            dateFormat="HH:mm"
                            required={required !== false}
                        />
                    </div>
                )
            }
        default:
            return null;
    }
}
